import React from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  type AssessmentFrequency,
  type AssessmentModalBanner,
  type AssessmentOption,
  type FrequencyOption,
} from './assessmentModalHelpers'

const MESSAGES = defineMessages({
  assessmentLabel: {
    defaultMessage: 'Assessment',
    description: 'Label for the assessment selector',
  },
  frequencyLabel: {
    defaultMessage: 'Frequency',
    description: 'Label for the assessment frequency selector',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Accessibility label for the button that closes the add or edit assessment modal',
  },
})

const BASE_INPUT_STYLE = { marginBottom: 0 }

const CORE_ASSESSMENTS_URL =
  'https://provider-support.lyrahealth.com/hc/en-us/articles/5112622889619-Client-Outcomes-Survey-Preview'

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '494px',
  alignSelf: 'center',
  gap: spacing['16px'],
}))

const HeaderContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

const FieldContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

const AssessmentSelectField = styled(SelectField<string>).attrs<{ theme: ThemeType }>(
  ({ disabled, theme: { colors } }) => {
    const inactiveInputStyle = disabled
      ? {
          backgroundColor: colors.inputBackgroundInactive,
          borderColor: colors.inputOutlineInactive,
          color: colors.inputTextInactive,
        }
      : undefined

    return {
      customWebStyle: inactiveInputStyle,
      customIOSStyle: inactiveInputStyle,
      customAndroidStyle: inactiveInputStyle,
    }
  },
)({})

const FrequencySelectField = styled(SelectField<AssessmentFrequency | ''>).attrs<{ theme: ThemeType }>(
  ({ disabled, theme: { colors } }) => {
    const inactiveInputStyle = disabled
      ? {
          backgroundColor: colors.inputBackgroundInactive,
          borderColor: colors.inputOutlineInactive,
          color: colors.inputTextInactive,
        }
      : undefined

    return {
      customWebStyle: inactiveInputStyle,
      customIOSStyle: inactiveInputStyle,
      customAndroidStyle: inactiveInputStyle,
    }
  },
)({})

const ActionsContainer = styled.View<{ hasBanner: boolean; theme: ThemeType }>(({ hasBanner, theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  ...(!hasBanner && { marginTop: spacing['16px'] }),
}))

export type AssessmentModalFormProps = {
  visible: boolean
  mode: 'add' | 'edit'
  title: string
  assessmentValue: string
  assessmentOptions: AssessmentOption[]
  assessmentDisabled?: boolean
  onAssessmentChange: (value: string) => void
  frequency: AssessmentFrequency | ''
  frequencyOptions: FrequencyOption[]
  frequencyDisabled?: boolean
  frequencyAccessibilityLabel?: string
  onFrequencyChange: (value: AssessmentFrequency | '') => void
  banner?: AssessmentModalBanner
  submitError?: string
  confirmText: React.ReactNode
  canSubmit: boolean
  isLoading: boolean
  onConfirm: () => void
  onRequestClose: () => void
  onEditExisting?: () => void
}

export const AssessmentModalForm: React.FC<AssessmentModalFormProps> = ({
  visible,
  mode,
  title,
  assessmentValue,
  assessmentOptions,
  assessmentDisabled = false,
  onAssessmentChange,
  frequency,
  frequencyOptions,
  frequencyDisabled = false,
  frequencyAccessibilityLabel,
  onFrequencyChange,
  banner,
  submitError,
  confirmText,
  canSubmit,
  isLoading,
  onConfirm,
  onRequestClose,
  onEditExisting = () => undefined,
}) => {
  const { formatMessage } = useIntl()
  const { colors, spacing } = useTheme()
  const bannerMessage =
    banner?.action === 'edit' ? (
      <TextButton text={banner.message} onPress={onEditExisting} testID={tID(`AssessmentModal-${mode}-editExisting`)} />
    ) : (
      banner?.message
    )

  const modalContents = (
    <ContentContainer testID={tID(`AssessmentModal-${mode}`)}>
      <HeaderContainer>
        <Title size={TitleSize.LARGE} text={title} />
        <PressableOpacity
          onPress={onRequestClose}
          disabled={isLoading}
          accessibilityLabel={formatMessage(MESSAGES.close)}
          testID={tID(`AssessmentModal-${mode}-close`)}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </HeaderContainer>
      <BodyText
        size={BodyTextSize.DEFAULT}
        text={
          <FormattedMessage
            defaultMessage='Choose supplemental assessments tailored to your client. Lyra’s scheduled <link>core assessments</link> will still be sent automatically.'
            description='Description in the add or edit assessment modal'
            values={{
              link: (chunks: string) => (
                <Link
                  text={chunks}
                  testID={tID(`AssessmentModal-${mode}-coreAssessments`)}
                  size={BodyTextSize.DEFAULT}
                  onPress={() => window.open(CORE_ASSESSMENTS_URL, '_blank')}
                  underline
                />
              ),
            }}
          />
        }
      />

      <FieldContainer>
        <BodyText size={BodyTextSize.DEFAULT} bold text={formatMessage(MESSAGES.assessmentLabel)} />
        <AssessmentSelectField
          value={assessmentValue}
          name='assessment'
          onChange={onAssessmentChange}
          accessibilityLabel={formatMessage(MESSAGES.assessmentLabel)}
          options={assessmentOptions}
          disabled={assessmentDisabled}
          icon={
            assessmentDisabled ? (
              <ChevronIcon fillColor={colors.iconInactive} direction={ChevronIconDirection.DOWN} size={16} />
            ) : undefined
          }
          baseInputStyle={BASE_INPUT_STYLE}
          useStringValue
        />
      </FieldContainer>

      <FieldContainer>
        <BodyText size={BodyTextSize.DEFAULT} bold text={formatMessage(MESSAGES.frequencyLabel)} />
        <FrequencySelectField
          value={frequency}
          name='frequency'
          onChange={onFrequencyChange}
          accessibilityLabel={frequencyAccessibilityLabel ?? formatMessage(MESSAGES.frequencyLabel)}
          options={frequencyOptions}
          disabled={frequencyDisabled}
          icon={
            <ChevronIcon
              fillColor={frequencyDisabled ? colors.iconInactive : colors.iconDefault}
              direction={ChevronIconDirection.DOWN}
              size={16}
            />
          }
          baseInputStyle={BASE_INPUT_STYLE}
        />
      </FieldContainer>

      {banner?.type === 'warning' && (
        <InlineWarningBanner
          testId={`AssessmentModal-${mode}-frequencyWarning`}
          marginBottom={spacing['16px']}
          title={banner.title}
          text={bannerMessage}
        />
      )}

      {banner?.type === 'info' && (
        <InlineInfoBanner
          testId={`AssessmentModal-${mode}-frequencyInfo`}
          backgroundColor={colors.backgroundSection}
          textColor={colors.textSecondary}
          iconColor={colors.iconDefault}
          iconSize={24}
          marginBottom={spacing['16px']}
          title={banner.title}
          text={bannerMessage}
        />
      )}

      {submitError && (
        <InlineErrorBanner
          testId={`AssessmentModal-${mode}-submitError`}
          marginBottom={spacing['16px']}
          text={submitError}
        />
      )}

      <ActionsContainer hasBanner={banner !== undefined || submitError !== undefined}>
        <PrimaryButton
          disabled={!canSubmit}
          loading={isLoading}
          onPress={onConfirm}
          text={confirmText}
          testID={tID(`AssessmentModal-${mode}-confirm`)}
        />
      </ActionsContainer>
    </ContentContainer>
  )

  return (
    <Modal
      modalContents={modalContents}
      width='544px'
      scrollable={false}
      disableFullScreenModal
      disableBottomSheet
      visible={visible}
      onRequestClose={onRequestClose}
      closeOnScrim
      accessibilityLabel={title}
    />
  )
}
